import { Cell, Grid, TableFooter } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { AccordionControls } from 'components/accordion/useAccordionControl'
import { FieldStatusEnum } from 'components/form'
import { TableBox } from 'components/table'
import { usePagination } from 'components/table/usePagination'
import { TextStatus } from 'components/text/TextStatus'
import { useOrientacoesQuery } from 'graphql/hooks.generated'
import { LocalAtendimento, TipoAtendimentoEnum } from 'graphql/types.generated'
import { TextFilterType } from 'hooks/filter/model'
import { useFilter } from 'hooks/filter/useFilter'
import { noop } from 'lodash'
import { useCallback, useMemo, useState } from 'react'
import { emptyArrayOf } from 'util/array'
import { formatDate } from 'util/date/formatDate'
import { emptyMapOf } from 'util/map'
import { metaPath } from 'util/metaPath'
import { getEditAtendProfId } from 'view/atendimentos/atendimento-individual/util-atendimentoIndividual'
import { LotacaoFolhaRosto } from 'view/atendimentos/detail/folha-rosto/model'
import { LotacaoAtendimento } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { convertFormModelToRowModel, convertOrientacaoDatabaseToRowModel } from '../converter-orientacoes'
import { OrientacaoFilterModel, OrientacaoFormModel, OrientacaoRowModel } from '../model-orientacoes'
import { mergeSortOrientacoes } from '../util/mergeSortOrientacoes'
import { OrientacoesAccordionPanel } from './OrientacoesAccordionPanel'
import { OrientacoesTableActions } from './OrientacoesTableActions'
import { OrientacoesTableFilter } from './OrientacoesTableFilter'
import { OrientacoesTableRow } from './OrientacoesTableRow'

const metaRow = metaPath<OrientacaoRowModel>()
const metaFilter = metaPath<OrientacaoFilterModel>()

const getFilterTextTypeConfig = (isRetificacao: boolean): TextFilterType => ({
  filterField: metaFilter.texto,
  searchFields: isRetificacao
    ? [metaRow.descricao]
    : [
        metaRow.descricao,
        metaRow.atendimentoProfissional.lotacao.cbo.nome,
        metaRow.atendimentoProfissional.lotacao.profissional.nome,
      ],
  removeTagsOnFilter: true,
})

const handleFilterBySomenteMeus = (doFilter: boolean, profissionalId: ID) => (
  orientacaoRowModel: OrientacaoRowModel
) => {
  return doFilter ? orientacaoRowModel.atendimentoProfissional.lotacao.profissional.id === profissionalId : true
}

interface OrientacoesTableProps extends AccordionControls {
  orientacoesWithStatus?: ReadonlyMap<OrientacaoFormModel, FieldStatusEnum>
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  dataReferencia: Instant
  lotacao: LotacaoAtendimento | LotacaoFolhaRosto
  isAtendimentoObservacao?: boolean
  atendimentoProfissionalId?: ID
  idOrientacaoEmEdicao?: ID
  tipoAtendimento?: TipoAtendimentoEnum
  localAtendimento?: LocalAtendimento
  orientacoes?: OrientacaoFormModel[]
  onUpdateItem?(itemToUpdate: OrientacaoFormModel): void
  onRemoveItem?(itemToRemove: OrientacaoFormModel): void
  onEditClick?(cacheId: ID): void
  onResetItem?(itemToReset: OrientacaoFormModel): void
  readOnly?: boolean
  isRetificacao?: boolean
}

export function OrientacoesTable(props: Readonly<OrientacoesTableProps>) {
  const {
    orientacoesWithStatus = emptyMapOf<OrientacaoFormModel, FieldStatusEnum>(),
    atendimentoId,
    prontuarioId,
    cidadao,
    dataReferencia,
    lotacao,
    isAtendimentoObservacao = false,
    atendimentoProfissionalId,
    idOrientacaoEmEdicao = null,
    tipoAtendimento,
    localAtendimento,
    onEditClick = noop,
    onUpdateItem = noop,
    onRemoveItem = noop,
    onResetItem = noop,
    resetExpandedItems,
    readOnly,
    isRetificacao,
    ...accordionProps
  } = props

  const editAtendProfId = getEditAtendProfId(isAtendimentoObservacao, atendimentoProfissionalId)

  const { data, loading } = useOrientacoesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      orientacaoQueryInput: {
        prontuarioId,
        editAtendProfId,
      },
    },
    skip: isRetificacao,
  })

  const [sort, setSort] = useState<string[]>(['-data'])

  const isEditing = useCallback((cacheId: ID): boolean => cacheId !== undefined && cacheId === idOrientacaoEmEdicao, [
    idOrientacaoEmEdicao,
  ])

  const orientacoesCache = useMemo(
    () =>
      Array.from(orientacoesWithStatus).map(([value, status]) =>
        convertFormModelToRowModel(
          value,
          status,
          dataReferencia,
          atendimentoProfissionalId,
          atendimentoId,
          lotacao,
          tipoAtendimento,
          localAtendimento
        )
      ) ?? emptyArrayOf<OrientacaoRowModel>(),
    [
      atendimentoId,
      atendimentoProfissionalId,
      dataReferencia,
      localAtendimento,
      lotacao,
      orientacoesWithStatus,
      tipoAtendimento,
    ]
  )

  const orientacoesDb = useMemo(
    () => data.orientacoes?.map(convertOrientacaoDatabaseToRowModel) ?? emptyArrayOf<OrientacaoRowModel>(),
    [data.orientacoes]
  )

  const todasOrientacoes = useMemo(() => mergeSortOrientacoes(orientacoesDb, orientacoesCache, sort), [
    orientacoesCache,
    orientacoesDb,
    sort,
  ])

  const components = useMemo(
    () => ({
      AccordionPanel: OrientacoesAccordionPanel,
      Row: (props) => (
        <OrientacoesTableRow
          isEditing={isEditing(props.row._id)}
          onUpdate={onUpdateItem}
          removeOrientacaoEmEdicao={() => onEditClick(null)}
          {...props}
        />
      ),
    }),
    [isEditing, onEditClick, onUpdateItem]
  )

  const [filter, setFilter] = useState<OrientacaoFilterModel>({ texto: '', somenteMeus: false })

  const orientacoesFiltradas = useFilter<OrientacaoRowModel, OrientacaoFilterModel>({
    items: todasOrientacoes,
    filter: filter,
    filtersType: [getFilterTextTypeConfig(isRetificacao)],
    ...(!isRetificacao && { customFilters: [handleFilterBySomenteMeus(filter.somenteMeus, lotacao.profissional.id)] }),
  })

  const { paginatedItems, tableProps } = usePagination<OrientacaoRowModel>({
    items: orientacoesFiltradas,
    onChange: resetExpandedItems,
  })

  return (
    <TableBox
      header={
        <OrientacoesTableFilter onChangeFilter={setFilter} currentFilter={filter} isRetificacao={isRetificacao} />
      }
    >
      <AccordionDataTable<OrientacaoRowModel>
        {...accordionProps}
        loading={loading}
        rows={paginatedItems}
        columns={[
          {
            name: 'data',
            header: 'Data',
            render: (item: OrientacaoRowModel) => (
              <TextStatus status={item.status}>{formatDate(item.atendimentoProfissional.iniciadoEm)}</TextStatus>
            ),
            size: 2,
            sortable: true,
          },
          {
            name: 'profissional',
            header: 'Profissional',
            render: (item: OrientacaoRowModel) => (
              <TextStatus status={item.status}>
                {item.atendimentoProfissional.lotacao.profissional.nome} <br />
                {item.atendimentoProfissional.lotacao.cbo.nome}
              </TextStatus>
            ),
            size: 4,
            sortable: true,
          },
          {
            name: 'descricao',
            header: 'Orientação',
            render: (item: OrientacaoRowModel) => {
              return (
                <Grid gap={1} gapVertical={0} alignItems='center' wrap={false}>
                  <Cell size={8}>
                    <TextStatus status={item.status}>{item.descricao.removeTags().truncate(100)}</TextStatus>
                  </Cell>
                  <Cell size={4} onClick={(e) => e.stopPropagation()}>
                    <OrientacoesTableActions
                      item={item}
                      controlModifiedStatus={isRetificacao}
                      atendimentoId={atendimentoId}
                      prontuarioId={prontuarioId}
                      cidadao={cidadao}
                      readOnly={readOnly}
                      onRemove={onRemoveItem}
                      onReset={onResetItem}
                      onEditClick={onEditClick}
                    />
                  </Cell>
                </Grid>
              )
            },
            size: 6,
          },
        ]}
        components={components}
        sort={sort}
        onSortChange={setSort}
        disableRow={(row) => isEditing(row._id)}
      />
      <TableFooter {...tableProps} />
    </TableBox>
  )
}
